// ABOUTME: Global search input component for the PimAppBar with grouped dropdown results
// ABOUTME: Searches configured hierarchies, renders results grouped by hierarchy under their configured titles

import {
    ArrowForwardIcon,
    Box,
    CircularProgress,
    List,
    ListItem,
    Paper,
    Popper,
    SearchIcon,
    TextField,
    Typography,
    useTheme,
} from '@ds/coolshop';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { trackAction } from '@src/_new/modules/user-analytics';
import { redirect } from '@src/_new/utils/redirect';
import { SettingsContext } from '@src/modules/setting/SettingsContext';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useGlobalSearch } from '../hooks/useGlobalSearch';

// Width of the search field in the app bar, as in the visuals.
const FIELD_WIDTH = 260;
// The results panel is wider than the field, so result names rarely wrap.
const PANEL_MIN_WIDTH = 350;

/** A hierarchy code as a title, when no label is configured: "PRICE_LIST" reads "Price list". */
const titleFromCode = (code: string) => {
    const words = code.toLowerCase().replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
};

export const GlobalSearchInput = () => {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const settings = useContext(SettingsContext);
    const groupLabels = settings.globalSearch?.groupLabels;
    const labelByHierarchy = useMemo(
        () =>
            new Map(
                // A blank label reads as none: the group keeps its code-derived title.
                (groupLabels ?? [])
                    .filter(entry => entry.label.trim())
                    .map(entry => [entry.hierarchyId, entry.label.trim()]),
            ),
        [groupLabels],
    );
    const { searchTerm, setSearchTerm, results, loading, error, open, setOpen } = useGlobalSearch();
    const anchorRef = useRef<HTMLDivElement>(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

    const allItems = results.flatMap(group =>
        group.items.map(item => ({ ...item, hierarchyId: group.hierarchyId })),
    );

    const handleSelect = useCallback(
        (hierarchyId: string, nodeId: string) => {
            // Only the hierarchy code is recorded, never the query text.
            trackAction('search.open-result', {
                resultType:
                    results.find(group => group.hierarchyId === hierarchyId)?.hierarchyCode ?? null,
            });
            setOpen(false);
            redirect({ hierarchy: hierarchyId, node: nodeId });
        },
        [setOpen, results],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpen(false);
                setFocusedIndex(-1);
                return;
            }
            if (!open || allItems.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const next = focusedIndex < allItems.length - 1 ? focusedIndex + 1 : 0;
                setFocusedIndex(next);
                itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const prev = focusedIndex > 0 ? focusedIndex - 1 : allItems.length - 1;
                setFocusedIndex(prev);
                itemRefs.current[prev]?.scrollIntoView({ block: 'nearest' });
            } else if (e.key === 'Enter' && focusedIndex >= 0) {
                e.preventDefault();
                const item = allItems[focusedIndex];
                if (!item) return;
                handleSelect(item.hierarchyId, item.nodeId);
            }
        },
        [open, allItems, focusedIndex, handleSelect, setOpen],
    );

    useEffect(() => {
        setFocusedIndex(-1);
        itemRefs.current = [];
    }, [results]);

    const showPopper = open && !!(loading || error || results.length > 0 || searchTerm.length >= 2);

    let itemIndex = 0;

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={anchorRef} width={FIELD_WIDTH}>
                {/* The search icon closes the field, as in the visuals; the spinner takes its place
                    while the results load. */}
                <TextField
                    size="medium"
                    fullWidth
                    placeholder={formatMessage({
                        id: 'global-search.placeholder',
                        defaultMessage: 'Search...',
                    })}
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (searchTerm.length >= 2 && results.length > 0) {
                            setOpen(true);
                        }
                    }}
                    endAdornment={
                        loading ? (
                            <CircularProgress size="small" />
                        ) : (
                            <SearchIcon fontSize="small" />
                        )
                    }
                />
                {/* The Popper root is portalled to the body and opens its own stacking context, so
                    the z-index goes on it, the app's always-on-top layer (as CustomPopper's): it keeps
                    the results above the sticky bars, the drawers and the cart's modal drawer. */}
                <Popper
                    open={showPopper}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    slotProps={{ root: { style: { zIndex: theme.zIndex.topComponent } } }}
                >
                    <Paper elevation={4} overflow="auto">
                        <Box
                            maxHeight={480}
                            minWidth={PANEL_MIN_WIDTH}
                            mt={0.5}
                            py={1}
                            overflow="auto"
                        >
                            {error && (
                                <Box p={2}>
                                    <Typography color="error">
                                        {formatMessage({
                                            id: 'global-search.error',
                                            defaultMessage: 'Search unavailable',
                                        })}
                                    </Typography>
                                </Box>
                            )}
                            {!error &&
                                !loading &&
                                results.length === 0 &&
                                searchTerm.length >= 2 && (
                                    <Box p={2}>
                                        <Typography color="textSecondary">
                                            {formatMessage({
                                                id: 'global-search.no-results',
                                                defaultMessage: 'No results',
                                            })}
                                        </Typography>
                                    </Box>
                                )}
                            {!error &&
                                results.map(group => {
                                    const groupItems = group.items.map(item => {
                                        const currentIndex = itemIndex;
                                        itemIndex += 1;
                                        return (
                                            <ListItem
                                                key={`${group.hierarchyId}-${item.nodeId}`}
                                                ref={el => {
                                                    itemRefs.current[currentIndex] = el;
                                                }}
                                                onClick={() =>
                                                    handleSelect(group.hierarchyId, item.nodeId)
                                                }
                                                cursor="pointer"
                                                selected={focusedIndex === currentIndex}
                                                // The result name fills the row, its arrow closes it.
                                                fullWidth
                                                divider={false}
                                                endAdornment={<ArrowForwardIcon fontSize="small" />}
                                            >
                                                <Typography variant="body2" noWrap>
                                                    {item.listName || item.code}
                                                </Typography>
                                            </ListItem>
                                        );
                                    });

                                    return (
                                        <Box key={group.hierarchyId}>
                                            <Box px={2} pt={1.5}>
                                                <Typography variant="subtitle2" color="secondary">
                                                    {labelByHierarchy.get(group.hierarchyId) ??
                                                        titleFromCode(group.hierarchyCode)}
                                                </Typography>
                                            </Box>
                                            <List>{groupItems}</List>
                                        </Box>
                                    );
                                })}
                        </Box>
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};
